import { Component, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { BomMaster, BomService } from 'src/app/shared/services/bom.service';
import { AiBomLine, AiBomQuote, AiBomService, AiMaterialRate } from 'src/app/shared/services/ai-bom.service';
import { ApiService } from 'src/app/shared/services/api.service';
import { ToastrMsgService } from 'src/app/shared/services/shared-services/toastr.service';

export type AiBomView = 'quote' | 'rates';

@Component({
  selector: 'app-ai-bom',
  templateUrl: './ai-bom.component.html',
  styleUrls: ['./ai-bom.component.css']
})
export class AiBomComponent implements OnInit, OnDestroy {
  private destroy$ = new Subject<void>();

  view: AiBomView = 'quote';
  boms: BomMaster[] = [];
  selectedBomId: number | null = null;
  quote: AiBomQuote | null = null;
  selectedIds = new Set<number>();
  draftRates: Record<number, number> = {};
  loadingList = false;
  loadingQuote = false;
  applying = false;
  savingMaterialId: number | null = null;

  rates: AiMaterialRate[] = [];
  materials: any[] = [];
  loadingRates = false;
  savingRate = false;
  newMaterialId: number | null = null;
  newUnitRate: number | null = null;
  newRegion = '';

  constructor(
    private bomService: BomService,
    private aiBomService: AiBomService,
    private api: ApiService,
    private toastr: ToastrMsgService,
    private route: ActivatedRoute,
    private router: Router
  ) {}

  ngOnInit(): void {
    const fromQuery = Number(this.route.snapshot.queryParamMap.get('bom_id'));
    if (Number.isFinite(fromQuery) && fromQuery > 0) {
      this.selectedBomId = fromQuery;
    }
    const tab = this.route.snapshot.queryParamMap.get('tab');
    this.view = tab === 'rates' ? 'rates' : 'quote';
    this.loadBoms();
    if (this.view === 'rates') {
      this.loadRates();
      this.loadMaterials();
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  setView(view: AiBomView): void {
    if (this.view === view) return;
    this.view = view;
    this.router.navigate([], {
      relativeTo: this.route,
      queryParams: { tab: view === 'rates' ? 'rates' : null },
      queryParamsHandling: 'merge'
    });
    if (view === 'rates') {
      this.loadRates();
      this.loadMaterials();
    }
  }

  loadBoms(): void {
    this.loadingList = true;
    this.bomService.getBoms({ page: 1, limit: 200 })
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (res) => {
          this.boms = res.data || [];
          this.loadingList = false;
          if (this.selectedBomId) {
            this.loadQuote();
          }
        },
        error: () => {
          this.loadingList = false;
          this.toastr.error('Failed to load BOMs');
        }
      });
  }

  onBomChange(): void {
    this.quote = null;
    this.selectedIds.clear();
    this.draftRates = {};
    if (this.selectedBomId) {
      this.router.navigate([], {
        relativeTo: this.route,
        queryParams: { bom_id: this.selectedBomId },
        queryParamsHandling: 'merge'
      });
      this.loadQuote();
    }
  }

  loadQuote(): void {
    if (!this.selectedBomId) return;
    this.loadingQuote = true;
    this.aiBomService.quote(this.selectedBomId)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (res) => {
          this.quote = res.data;
          this.selectedIds = new Set((res.data.lines || []).map((l) => l.bom_material_id));
          this.draftRates = {};
          for (const line of res.data.lines || []) {
            if (this.canSetLiveRate(line) && line.material_id) {
              this.draftRates[line.material_id] = Number(line.current_rate) || 0;
            }
          }
          this.loadingQuote = false;
        },
        error: (err) => {
          this.loadingQuote = false;
          this.toastr.error(err.error?.message || 'Failed to quote current rates');
        }
      });
  }

  isSelected(line: AiBomLine): boolean {
    return this.selectedIds.has(line.bom_material_id);
  }

  toggleLine(line: AiBomLine, checked: boolean): void {
    if (checked) this.selectedIds.add(line.bom_material_id);
    else this.selectedIds.delete(line.bom_material_id);
  }

  selectedCount(): number {
    return this.selectedIds.size;
  }

  canApplyInPlace(): boolean {
    const status = this.quote?.bom?.status;
    return status === 'draft' || status === 'pending';
  }

  canSetLiveRate(line: AiBomLine): boolean {
    return !!line.material_id && (line.source === 'catalog' || line.source === 'none');
  }

  formatInr(value: number | null | undefined): string {
    const n = Number(value) || 0;
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      maximumFractionDigits: 2
    }).format(n);
  }

  formatPct(value: number | null | undefined): string {
    const n = Number(value) || 0;
    const sign = n > 0 ? '+' : '';
    return `${sign}${n.toFixed(1)}%`;
  }

  materialLabel(material: any): string {
    const code = material?.system_code || material?.code || '';
    const name = material?.name || material?.material_name || 'Material';
    return code ? `${code} — ${name}` : name;
  }

  applyRates(): void {
    if (!this.selectedBomId || !this.quote) return;
    if (!this.canApplyInPlace()) {
      this.toastr.warning('Approved BOMs cannot be overwritten. Save as a new version.');
      return;
    }
    if (!confirm(`Update ${this.selectedCount()} line(s) on ${this.quote.bom.bom_code} with current rates?`)) {
      return;
    }
    this.applying = true;
    this.aiBomService.apply(this.selectedBomId, Array.from(this.selectedIds))
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (res) => {
          this.quote = res.data;
          this.applying = false;
          this.toastr.success(res.message || 'Rates applied');
        },
        error: (err) => {
          this.applying = false;
          this.toastr.error(err.error?.message || 'Failed to apply rates');
        }
      });
  }

  saveAsVersion(): void {
    if (!this.selectedBomId || !this.quote) return;
    if (!confirm(`Create a new draft BOM from ${this.quote.bom.bom_code} using current rates?`)) {
      return;
    }
    this.applying = true;
    this.aiBomService.saveAsVersion(this.selectedBomId, Array.from(this.selectedIds))
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (res) => {
          this.applying = false;
          this.toastr.success(res.message || `Created ${res.data.bom_code}`);
          this.selectedBomId = res.data.bom_id;
          this.loadBoms();
        },
        error: (err) => {
          this.applying = false;
          this.toastr.error(err.error?.message || 'Failed to create BOM version');
        }
      });
  }

  saveInlineRate(line: AiBomLine): void {
    if (!line.material_id) return;
    const unitRate = Number(this.draftRates[line.material_id]);
    if (!Number.isFinite(unitRate) || unitRate < 0) {
      this.toastr.error('Enter a valid live rate');
      return;
    }
    this.savingMaterialId = line.material_id;
    this.aiBomService.saveRate({
      material_id: line.material_id,
      unit_rate: unitRate,
      source: 'manual'
    })
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: () => {
          this.savingMaterialId = null;
          this.toastr.success('Live rate saved');
          this.loadQuote();
        },
        error: (err) => {
          this.savingMaterialId = null;
          this.toastr.error(err.error?.message || 'Failed to save live rate');
        }
      });
  }

  loadMaterials(): void {
    if (this.materials.length) return;
    this.api.getMaterialsMaster()
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (res: any) => {
          this.materials = res?.data || res || [];
        },
        error: () => {
          this.materials = [];
          this.toastr.error('Failed to load materials');
        }
      });
  }

  loadRates(): void {
    this.loadingRates = true;
    this.aiBomService.listRates()
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (res) => {
          this.rates = res.data || [];
          this.loadingRates = false;
        },
        error: (err) => {
          this.loadingRates = false;
          this.toastr.error(err.error?.message || 'Failed to load live rates');
        }
      });
  }

  addLiveRate(): void {
    if (!this.newMaterialId) {
      this.toastr.error('Select a material');
      return;
    }
    const unitRate = Number(this.newUnitRate);
    if (!Number.isFinite(unitRate) || unitRate < 0) {
      this.toastr.error('Enter a valid unit rate');
      return;
    }
    this.savingRate = true;
    this.aiBomService.saveRate({
      material_id: this.newMaterialId,
      unit_rate: unitRate,
      source: 'manual',
      region: this.newRegion.trim() || undefined
    })
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: () => {
          this.savingRate = false;
          this.newMaterialId = null;
          this.newUnitRate = null;
          this.newRegion = '';
          this.toastr.success('Live rate saved');
          this.loadRates();
          if (this.selectedBomId) this.loadQuote();
        },
        error: (err) => {
          this.savingRate = false;
          this.toastr.error(err.error?.message || 'Failed to save live rate');
        }
      });
  }

  removeLiveRate(row: AiMaterialRate): void {
    if (!row.owned) {
      this.toastr.warning('Inherited global rates cannot be deleted here.');
      return;
    }
    if (!confirm(`Remove the live rate for ${row.material_code || row.material_name || row.material_id}? Next quote will fall back to catalog.`)) {
      return;
    }
    this.aiBomService.deleteRate(row.id)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (res) => {
          this.toastr.success(res.message || 'Live rate removed');
          this.loadRates();
          if (this.selectedBomId) this.loadQuote();
        },
        error: (err) => {
          this.toastr.error(err.error?.message || 'Failed to delete live rate');
        }
      });
  }
}
